import { useEffect, useState } from 'react';
import { FileText, Plus, Trash2, Upload, Pencil, ExternalLink, ShieldCheck } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import SprayNav from '../../components/spray/SprayNav';
import Button from '../../components/ui/Button';
import CredentialStatusBadge from '../../components/spray/CredentialStatusBadge';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import AccessDenied from '../../components/ui/ModuleAccessDenied';
import {
  listCredentialTypes,
  listMyCredentials,
  createCredential,
  updateCredential,
  deleteCredential,
  openCredentialDocument,
  type SprayCredentialType,
  type UserSprayCredential,
} from '../../services/sprayCredential';
import { formatLocalDateInTz } from '../../utils/timezone';
import { useOrgTimezone } from '../../utils/timezone';

interface EditingState {
  id: number | null; // null = new
  credential_type_id: number | '';
  credential_number: string;
  issuer: string;
  issued_date: string;
  expiry_date: string;
  notes: string;
  document: File | null;
}

const emptyForm = (): EditingState => ({
  id: null,
  credential_type_id: '',
  credential_number: '',
  issuer: '',
  issued_date: '',
  expiry_date: '',
  notes: '',
  document: null,
});

function SprayMyCredentials() {
  const license = useModuleLicense('spray', 'operator');
  const creatorLicense = useModuleLicense('spray', 'creator');
  const orgTz = useOrgTimezone();

  const [types, setTypes] = useState<SprayCredentialType[]>([]);
  const [credentials, setCredentials] = useState<UserSprayCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [docError, setDocError] = useState<string | null>(null);

  const viewDocument = async (c: UserSprayCredential) => {
    setDocError(null);
    try {
      await openCredentialDocument(c);
    } catch {
      setDocError(`Could not open ${c.document_original_name || 'the document'}. Try again.`);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [typesRes, credsRes] = await Promise.all([
        listCredentialTypes(false),
        listMyCredentials(),
      ]);
      if (typesRes.success) setTypes(typesRes.data);
      if (credsRes.success) setCredentials(credsRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const hasAccess = license.hasAccess || creatorLicense.hasAccess;

  if (license.loading && creatorLicense.loading) return null;
  if (!hasAccess) {
    return <AccessDenied moduleName="Spray" />;
  }

  const startEdit = (c: UserSprayCredential) => {
    setEditing({
      id: c.id,
      credential_type_id: c.credential_type_id,
      credential_number: c.credential_number || '',
      issuer: c.issuer || '',
      issued_date: c.issued_date || '',
      expiry_date: c.expiry_date || '',
      notes: c.notes || '',
      document: null,
    });
    setError(null);
  };

  const startNew = () => {
    setEditing(emptyForm());
    setError(null);
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.credential_type_id) {
      setError('Please select a credential type.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editing.id == null) {
        const res = await createCredential({
          credential_type_id: Number(editing.credential_type_id),
          credential_number: editing.credential_number || undefined,
          issuer: editing.issuer || undefined,
          issued_date: editing.issued_date || undefined,
          expiry_date: editing.expiry_date || undefined,
          notes: editing.notes || undefined,
          document: editing.document,
        });
        if (!res.success) throw new Error('Save failed');
      } else {
        const res = await updateCredential(editing.id, {
          credential_number: editing.credential_number,
          issuer: editing.issuer,
          issued_date: editing.issued_date,
          expiry_date: editing.expiry_date,
          notes: editing.notes,
          document: editing.document,
        });
        if (!res.success) throw new Error('Save failed');
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    if (!confirm('Delete this credential record?')) return;
    await deleteCredential(id);
    await load();
  };

  return (
    <>
      <PageHeader
        title="My Spray Credentials"
        subtitle="Record your Chemical Users License, ACUP and other qualifications"
        icon={<ShieldCheck className="h-6 w-6 text-white" />}
      />
      <SprayNav />
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="mb-4 flex justify-end">
          <Button size="sm" onClick={startNew}>
            <Plus className="h-4 w-4 mr-1" /> Add credential
          </Button>
        </div>

        {docError && <p className="mb-3 text-sm text-rose-600">{docError}</p>}

        {loading ? (
          <div className="text-sm text-neutral-500">Loading…</div>
        ) : credentials.length === 0 ? (
          <div className="border border-dashed border-neutral-300 rounded-xl p-10 text-center">
            <FileText className="h-8 w-8 mx-auto text-neutral-400 mb-2" />
            <p className="text-sm text-neutral-600">No credentials recorded yet.</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {credentials.map(c => (
              <div key={c.id} className="border border-neutral-200 bg-white rounded-xl p-4 flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-neutral-900">{c.credential_type_name}</h3>
                    <CredentialStatusBadge status={c.status} />
                    {c.is_required_for_operators && (
                      <span className="text-xs text-neutral-500">(required)</span>
                    )}
                  </div>
                  <div className="text-sm text-neutral-600 space-y-0.5">
                    {c.credential_number && <p>Number: {c.credential_number}</p>}
                    {c.issuer && <p>Issuer: {c.issuer}</p>}
                    {c.issued_date && <p>Issued: {formatLocalDateInTz(c.issued_date, orgTz)}</p>}
                    {c.expiry_date && <p>Expires: {formatLocalDateInTz(c.expiry_date, orgTz)}</p>}
                    {c.notes && <p className="text-neutral-500 italic">{c.notes}</p>}
                    {c.has_document && (
                      <button
                        type="button"
                        onClick={() => viewDocument(c)}
                        className="text-primary-600 hover:underline inline-flex items-center gap-1 text-xs text-left break-all"
                      >
                        <ExternalLink className="h-3 w-3 flex-shrink-0" />
                        {c.document_original_name || 'View document'}
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(c)} className="p-1.5 text-neutral-500 hover:text-primary-600" aria-label="Edit">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(c.id)} className="p-1.5 text-neutral-500 hover:text-rose-600" aria-label="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {editing && (
          <CredentialFormModal
            state={editing}
            setState={setEditing}
            types={types}
            onClose={() => setEditing(null)}
            onSave={save}
            saving={saving}
            error={error}
            isNew={editing.id == null}
          />
        )}
      </div>
    </>
  );
}

interface ModalProps {
  state: EditingState;
  setState: (s: EditingState) => void;
  types: SprayCredentialType[];
  onClose: () => void;
  onSave: () => void;
  saving: boolean;
  error: string | null;
  isNew: boolean;
}

function CredentialFormModal({ state, setState, types, onClose, onSave, saving, error, isNew }: ModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <header className="px-5 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-semibold">{isNew ? 'Add credential' : 'Edit credential'}</h2>
        </header>
        <div className="px-5 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
          {isNew && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Credential type</label>
              <select
                value={state.credential_type_id}
                onChange={e => setState({ ...state, credential_type_id: e.target.value ? Number(e.target.value) : '' })}
                className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
              >
                <option value="">Select…</option>
                {types.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name}{t.is_required_for_operators ? ' (required)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Credential number</label>
              <input
                type="text"
                value={state.credential_number}
                onChange={e => setState({ ...state, credential_number: e.target.value })}
                className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Issuer</label>
              <input
                type="text"
                value={state.issuer}
                onChange={e => setState({ ...state, issuer: e.target.value })}
                className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Issued date</label>
              <input
                type="date"
                value={state.issued_date}
                onChange={e => setState({ ...state, issued_date: e.target.value })}
                className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Expiry date</label>
              <input
                type="date"
                value={state.expiry_date}
                onChange={e => setState({ ...state, expiry_date: e.target.value })}
                className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
            <textarea
              value={state.notes}
              onChange={e => setState({ ...state, notes: e.target.value })}
              rows={2}
              className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Document (PDF or image, max 10 MB)</label>
            <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-1.5 rounded-md bg-neutral-100 border border-neutral-300 text-sm hover:bg-neutral-200">
              <Upload className="h-4 w-4" />
              {state.document ? state.document.name : 'Choose file'}
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*"
                onChange={e => setState({ ...state, document: e.target.files?.[0] || null })}
                className="hidden"
              />
            </label>
          </div>
          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>
        <footer className="px-5 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-xl flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={onSave} isLoading={saving}>Save</Button>
        </footer>
      </div>
    </div>
  );
}

export default SprayMyCredentials;
